import { createAsyncThunk } from "@reduxjs/toolkit";
import type {
  BookingsListResponse,
  CreateBookingRequest,
  CreateBookingResponse,
  DeleteBookingRequest,
  DeleteBookingResponse,
  BookingDeletePreviewResponse,
  UpdateBookingRequest,
  UpdateBookingResponse,
} from "api-client";
import { rejectMessage } from "api-client";
import { bookingsService } from "../../services/bookings.service";
import type { BookingsListState } from "./bookings.models";

type BookingsListRootSlice = { bookingsList: BookingsListState };

function resolveCustomerIds(q: BookingsListState): number[] {
  if (q.scopeCustomerId != null && q.scopeCustomerId.trim() !== "") {
    const n = parseInt(q.scopeCustomerId.trim(), 10);
    return Number.isFinite(n) ? [n] : [];
  }
  return q.customerFilterIds.map((id) => parseInt(id.trim(), 10)).filter((n) => Number.isFinite(n));
}

function resolveLocationIds(q: BookingsListState): number[] {
  return q.locationFilterIds.map((id) => parseInt(id.trim(), 10)).filter((n) => Number.isFinite(n));
}

function resolveCarrierIds(q: BookingsListState): number[] {
  if (q.scopeCarrierId != null && q.scopeCarrierId.trim() !== "") {
    const n = parseInt(q.scopeCarrierId.trim(), 10);
    return Number.isFinite(n) ? [n] : [];
  }
  return q.carrierFilterIds.map((id) => parseInt(id.trim(), 10)).filter((n) => Number.isFinite(n));
}

function resolveDateRangeForQuery(q: BookingsListState): { dateFrom: string | null; dateTo: string | null } {
  const df = q.dateFrom?.trim() || null;
  const dt = q.dateTo?.trim() || null;
  if (df && !dt) return { dateFrom: df, dateTo: df };
  return { dateFrom: df, dateTo: dt };
}

function resolveStatusValues(q: BookingsListState): string[] | undefined {
  const list = q.statusFilterValues.map((s) => s.trim().toUpperCase()).filter(Boolean);
  return list.length > 0 ? list : undefined;
}

function resolveBookingTypeValues(q: BookingsListState): string[] | undefined {
  const list = q.bookingTypeFilterValues
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s === "pickup" || s === "dropoff");
  return list.length > 0 ? list : undefined;
}

export const fetchBookings = createAsyncThunk<
  BookingsListResponse,
  void,
  { state: BookingsListRootSlice; rejectValue: string }
>("bookingsList/fetch", async (_, { getState, rejectWithValue }) => {
  const q = getState().bookingsList;
  try {
    const { dateFrom, dateTo } = resolveDateRangeForQuery(q);
    const customerIds = resolveCustomerIds(q);
    return await bookingsService.getBookings({
      customerId: null,
      customerIds: customerIds.length > 0 ? customerIds : undefined,
      carrierIds: resolveCarrierIds(q),
      locationIds: resolveLocationIds(q),
      searchByName: q.search.trim(),
      dateFrom,
      dateTo,
      statusValues: resolveStatusValues(q),
      bookingTypeValues: resolveBookingTypeValues(q),
      paging: {
        fromIndex: q.fromIndex,
        toIndex: q.toIndex,
        sortingOrder: q.sortingOrder,
        sortingField: q.sortingField,
      },
    });
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to fetch bookings"));
  }
});

export const createBooking = createAsyncThunk<
  CreateBookingResponse,
  CreateBookingRequest,
  { rejectValue: string }
>("bookingsList/create", async (body, { rejectWithValue }) => {
  try {
    return await bookingsService.createBooking(body);
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to create booking"));
  }
});

/** GET .../bookings/{bookingId}/delete-preview (AV-48). */
export const fetchBookingDeletePreview = createAsyncThunk<
  BookingDeletePreviewResponse,
  number,
  { rejectValue: string }
>("bookingsList/fetchDeletePreview", async (bookingId, { rejectWithValue }) => {
  try {
    return await bookingsService.getBookingDeletePreview(bookingId);
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to load delete preview"));
  }
});

/** PUT .../bookings/{bookingId} (AV-44). */
export const updateBooking = createAsyncThunk<
  UpdateBookingResponse,
  UpdateBookingRequest,
  { rejectValue: string }
>("bookingsList/update", async (body, { rejectWithValue }) => {
  try {
    return await bookingsService.updateBooking(body.bookingId, body);
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to update booking"));
  }
});

/** DELETE .../bookings/{bookingId} (AV-48). */
export const deleteBooking = createAsyncThunk<
  DeleteBookingResponse,
  DeleteBookingRequest,
  { rejectValue: string }
>("bookingsList/delete", async (body, { rejectWithValue }) => {
  try {
    return await bookingsService.deleteBooking(body.bookingId, body);
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to delete booking"));
  }
});
